import { proxy } from 'valtio';
import { getPylonSsoUrl, validateToken } from './api/auth2';
import { setToken as apiSetToken } from './api/common';
import { isDeadSessionRejection } from './common/authUtils';
import { clearPosthogContext } from './common/posthog';
import { reportAuthError } from './common/reportAuthError';
import type { TLoadingState } from './common/types';
import sessionStore from './components2/TokenValidator/store';
import rootStore, { DISCLAIMER_VIEWED_KEY } from './store';

export const LoadingState: TLoadingState = { isLoading: true };

/** Only allow redirecting the Pylon SSO JWT to Pylon’s official JWT callback. */
function isTrustedPylonRedirectUri(uri: string): boolean {
  try {
    const url = new URL(uri);
    return (
      url.origin === 'https://graph.usepylon.com' &&
      url.pathname === '/callback/jwt' &&
      url.searchParams.get('orgSlug') === 'civilgrid'
    );
  } catch {
    return false;
  }
}

const nonRedirectPaths: string[] = ['register', 'login', 'join', 'headless'];

// Not a module variable: the SSO hop leaves the document for the IdP.
const PYLON_REDIRECT_KEY = 'com.civilgrid.pylonRedirect';

function storePylonRedirectUri(uri: string) {
  try {
    window.sessionStorage.setItem(PYLON_REDIRECT_KEY, uri);
  } catch {
    // Best-effort: not worth failing a login over.
  }
}

/** Re-validated on read, not only on write: sessionStorage is script-writable. */
function readStoredPylonRedirectUri(): string | undefined {
  try {
    const uri = window.sessionStorage.getItem(PYLON_REDIRECT_KEY);
    return uri && isTrustedPylonRedirectUri(uri) ? uri : undefined;
  } catch {
    return undefined;
  }
}

function clearStoredPylonRedirectUri() {
  try {
    window.sessionStorage.removeItem(PYLON_REDIRECT_KEY);
  } catch {
    // Best-effort, as above.
  }
}

type SessionHint = { email: string; userId: string };

class Store {
  STORAGE_KEY = 'com.civilgrid.auth';
  // Cookie sessions store this hint (NOT a JWT) so the app can render an authenticated UI
  // without an auth round-trip. Keep in sync with authUtils.SESSION_HINT_KEY. TODO(INFRA-218):
  // remove once cookie auth is the only web path.
  SESSION_HINT_KEY = 'com.civilgrid.session';
  tokenState: 'NOT_LOADED' | 'LOADING' | 'VALID' | 'INVALID' = 'NOT_LOADED';
  token?: string;
  email?: string;
  userId?: string;
  pylonEmailHash?: string;
  redirectUri?: string;

  get isTokenLoading() {
    return this.tokenState === 'NOT_LOADED' || this.tokenState === 'LOADING';
  }

  get isTokenValid() {
    return this.tokenState === 'VALID';
  }

  get isTokenInvalid() {
    return this.tokenState === 'INVALID';
  }

  /** Reads the cookie-session hint, or null if absent/corrupt. */
  private readSessionHint(): SessionHint | null {
    const raw = window.localStorage.getItem(this.SESSION_HINT_KEY);
    if (!raw) return null;
    try {
      const hint = JSON.parse(raw) as Partial<SessionHint>;
      if (typeof hint?.email === 'string' && typeof hint?.userId === 'string') {
        return { email: hint.email, userId: hint.userId };
      }
    } catch {
      // fall through
    }
    return null;
  }

  async init() {
    // Writing tokenState mounts the router, whose auth guard navigates away and drops this
    // query string.
    const rawPylonRedirectUri = this.getPylonRedirectUri();
    const pendingPylonRedirectUri =
      rawPylonRedirectUri && isTrustedPylonRedirectUri(rawPylonRedirectUri) ? rawPylonRedirectUri : undefined;
    if (rawPylonRedirectUri && !pendingPylonRedirectUri) {
      console.warn('Ignoring untrusted Pylon redirect_uri', rawPylonRedirectUri);
    }
    if (pendingPylonRedirectUri) {
      storePylonRedirectUri(pendingPylonRedirectUri);
    }

    this.tokenState = 'LOADING';
    // get token from localStorage if it exists
    const token = window.localStorage.getItem(this.STORAGE_KEY);
    if (token && token.length > 0) {
      apiSetToken(token);

      // validateToken is the bearer-token validity gate: a confirmed rejection means the token is
      // genuinely invalid/expired. Any other failure (network error, timeout, 5xx) means we
      // simply didn't hear back — mirror the cookie-session gate below and keep the optimistic
      // session; a genuinely bad token will be rejected on the next authenticated request.
      try {
        const { pylonEmailHash } = await validateToken();
        this.pylonEmailHash = pylonEmailHash;
      } catch (e) {
        if (isDeadSessionRejection(e)) {
          this.tokenState = 'INVALID';
          // clear stored token
          window.localStorage.removeItem(this.STORAGE_KEY);
          this.setRedirectUri(this.getRedirectUri());
          console.error(e);
          reportAuthError(e, { context: 'auth:init', step: 'legacyInit' });
          return;
        }
        console.error('validateToken failed during legacy init:', e);
      }

      // if a redirect_uri param exists (Pylon SSO), redirect through Pylon callback
      if (pendingPylonRedirectUri) {
        clearStoredPylonRedirectUri();
        try {
          const ssoUrl = await getPylonSsoUrl(pendingPylonRedirectUri);
          this.tokenState = 'VALID';
          this.token = token;
          window.localStorage.setItem(this.STORAGE_KEY, token);
          window.location.href = ssoUrl;
          return;
        } catch (e) {
          // Pylon being unreachable says nothing about whether this CivilGrid session is
          // valid — fall through to a normal login instead of stranding the user here.
          console.error('getPylonSsoUrl failed; continuing without the Pylon redirect:', e);
        }
      }

      // if a from_url param exists and login was successful,
      // redirect the user to where they were previously
      const to = this.getFromUrl();
      await this.setToken(token, to);
      return;
    }

    // Cookie-session path: no JWT, but a session hint means the user is logged in via the
    // HttpOnly auth cookie (sent automatically by the browser via withCredentials).
    // TODO(INFRA-218): remove this branch once cookie auth is the only web path.
    const hint = this.readSessionHint();
    if (hint) {
      // Optimistic: mark VALID from the hint so the shell renders without an auth spinner.
      apiSetToken('');
      this.email = hint.email;
      this.userId = hint.userId;
      this.tokenState = 'VALID';

      // validateToken is the cookie-validity gate: a rejection means the cookie is invalid/
      // expired, and the response interceptor's validateToken branch already raises the
      // session-expired dialog, so we just stop.
      try {
        const { pylonEmailHash } = await validateToken();
        this.pylonEmailHash = pylonEmailHash;
      } catch (e) {
        if (isDeadSessionRejection(e)) {
          // Invalid/expired cookie. The interceptor raises the session-expired dialog too;
          // clear our own state + hint here so authStore is self-consistent regardless of
          // interceptor timing.
          this.tokenState = 'INVALID';
          this.email = undefined;
          this.userId = undefined;
          window.localStorage.removeItem(this.SESSION_HINT_KEY);
          this.setRedirectUri(this.getRedirectUri());
          reportAuthError(e, { context: 'auth:init', step: 'cookieInit' });
          return;
        }
        // Transient failure (network/5xx): keep the optimistic session and bootstrap;
        // a genuinely bad cookie will surface on the next authenticated request.
        console.error('validateToken failed during cookie-session init:', e);
      }

      // Pylon SSO redirect (cookie authenticates getPylonSsoUrl), same as the JWT path.
      if (pendingPylonRedirectUri) {
        clearStoredPylonRedirectUri();
        try {
          const ssoUrl = await getPylonSsoUrl(pendingPylonRedirectUri);
          window.location.href = ssoUrl;
          return;
        } catch (e) {
          // Pylon being unreachable says nothing about whether this CivilGrid session is
          // valid — fall through to a normal login instead of stranding the user here.
          console.error('getPylonSsoUrl failed; continuing without the Pylon redirect:', e);
        }
      }

      const to = this.getFromUrl();
      await rootStore.bootstrap('');
      if (to) {
        window.location.href = to;
      }
      this.setRedirectUri();
      return;
    }

    // No token and no hint: go to the login screen.
    apiSetToken('');
    this.tokenState = 'INVALID';
    this.setRedirectUri(this.getRedirectUri());
  }

  async setToken(token: string, redirectTo?: string) {
    // Clears a stale expired-session flag, which the axios interceptor re-sets if validateToken rejects this session.
    sessionStore.reset();
    apiSetToken(token);
    this.tokenState = 'VALID';
    this.token = token;
    window.localStorage.setItem(this.STORAGE_KEY, token);

    try {
      const { pylonEmailHash } = await validateToken();
      this.pylonEmailHash = pylonEmailHash;
    } catch (e) {
      console.error('validateToken failed during setToken:', e);
      // Report on the auth page even though no user identity is set yet (unauthenticated).
      reportAuthError(e, { context: 'auth:setToken', step: 'setToken' });

      if (isDeadSessionRejection(e)) {
        this.tokenState = 'INVALID';
        this.token = undefined;
        apiSetToken('');
        window.localStorage.removeItem(this.STORAGE_KEY);
        this.setRedirectUri(this.getRedirectUri());
        return;
      }
      // Transient failure (network/5xx): keep the optimistic session and bootstrap;
      // a genuinely bad token will be rejected on the next authenticated request.
    }

    rootStore.bootstrap(token);

    if (await this.redirectToPylonIfPending()) return;

    if (redirectTo) {
      window.location.href = redirectTo;
    }

    this.setRedirectUri();
  }

  /** Takes precedence over the caller's own post-login destination. */
  private async redirectToPylonIfPending(): Promise<boolean> {
    const uri = readStoredPylonRedirectUri();
    if (!uri) return false;
    clearStoredPylonRedirectUri();
    try {
      window.location.href = await getPylonSsoUrl(uri);
      return true;
    } catch (e) {
      console.error('getPylonSsoUrl failed; continuing without the Pylon redirect:', e);
      return false;
    }
  }

  /**
   * Called after a cookie-based login ({ email, userId }, no token). Persists the session hint
   * and bootstraps using the cookie. Mirrors setToken minus the JWT — validateToken runs only to
   * fetch the Pylon hash (the cookie was just issued, so it is valid).
   * TODO(INFRA-218): the only login path once cookie auth is fully rolled out.
   */
  async setSessionHint(email: string, userId: string, redirectTo?: string) {
    // Clears a stale expired-session flag, which the axios interceptor re-sets if validateToken rejects this session.
    sessionStore.reset();
    apiSetToken('');
    // Drop any legacy bearer token. init() reads STORAGE_KEY before the session hint, so a
    // stale token left here would shadow the freshly-issued cookie on the next refresh and
    // bounce the user to /login when that token is expired/invalid. INFRA-218.
    window.localStorage.removeItem(this.STORAGE_KEY);
    this.email = email;
    this.userId = userId;
    this.tokenState = 'VALID';
    window.localStorage.setItem(this.SESSION_HINT_KEY, JSON.stringify({ email, userId }));

    // Populate the Pylon email hash before bootstrap builds the Pylon widget. Non-fatal
    // if it fails — the cookie was just issued, so this is not an auth gate here.
    try {
      const { pylonEmailHash } = await validateToken();
      this.pylonEmailHash = pylonEmailHash;
    } catch (e) {
      console.error('validateToken failed during setSessionHint:', e);
      // Non-fatal here (the cookie was just issued), so report as a warning — but still surface it
      // so we can see cookie-session Pylon-hash failures on the auth page.
      reportAuthError(e, { context: 'auth:setSessionHint', step: 'setSessionHint', severity: 'warning' });
    }

    rootStore.bootstrap('');

    if (await this.redirectToPylonIfPending()) return;

    if (redirectTo) {
      window.location.href = redirectTo;
    }

    this.setRedirectUri();
  }

  async reset() {
    window.pylon = undefined;
    clearPosthogContext();
    apiSetToken('');
    rootStore.me = null;
    this.tokenState = 'INVALID';
    this.token = undefined;
    this.email = undefined;
    this.userId = undefined;
    window.localStorage.removeItem(this.STORAGE_KEY);
    // Clear the cookie-session hint too. The HttpOnly cookie itself is cleared server-side
    // by the logOut API call the logout UI makes before reset(). INFRA-218.
    window.localStorage.removeItem(this.SESSION_HINT_KEY);
    // Clear disclaimer viewed flag so it shows again on next login (CG-958)
    window.localStorage.removeItem(DISCLAIMER_VIEWED_KEY);
    clearStoredPylonRedirectUri();
    this.setRedirectUri();
  }

  setRedirectUri(path?: string) {
    this.redirectUri = path;
  }

  getFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('from_url') || undefined;
  }

  getPylonRedirectUri() {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect_uri') || undefined;
  }

  getRedirectUri() {
    const fromUrl = this.getFromUrl();
    if (fromUrl) return fromUrl;

    // if not a support site link, then its an app link.
    if (!nonRedirectPaths.some((p) => window.location.pathname.includes(p))) {
      return window.location.href;
    }
  }
}

const store = proxy(new Store());

export default store;
